import { emitPendoEvent } from "@/util/pendoConfig";
import type { BuilderContext, RuleResult } from "./types";

/**
 * Rule-level telemetry.
 *
 * One event carrying the rule id, rather than an event per rule, so adding a
 * rule never means adding a Pendo event.
 *
 * Privacy boundary: rule findings carry interpolation params such as a field's
 * label or a CTA address, and those are user-entered content. Nothing here
 * reads `result.params` — only the rule id, the section, and the element's
 * type, none of which are user text. Counts are bucketed rather than exact so
 * a small Location cannot be identified from them.
 */
export const RULE_DETECTED = "builder_validation_rule_detected";
export const RULE_RESOLVED = "builder_validation_rule_resolved";

const countBucket = (count: number): string => {
  if (count === 0) return "0";
  if (count <= 5) return "1-5";
  if (count <= 10) return "6-10";
  if (count <= 25) return "11-25";
  return "25+";
};

const timeBucket = (elapsedMs: number): string => {
  const seconds = elapsedMs / 1000;
  if (seconds < 30) return "under_30s";
  if (seconds < 120) return "30s_2m";
  if (seconds < 600) return "2m_10m";
  return "over_10m";
};

const conditionalLogicCount = (ctx: BuilderContext): number => {
  const rules = ctx.builderConfig.conditionalLogic;
  return Array.isArray(rules) ? rules.length : 0;
};

const customFieldCount = (ctx: BuilderContext): number => {
  const ids = new Set<string>();
  ctx.elements.forEach(({ element, elementKey }) => {
    if (element.custom) ids.add(element.id || elementKey);
  });
  return ids.size;
};

/** The element's tag, which is a builder concept rather than user content. */
const elementTypeFor = (ctx: BuilderContext, result: RuleResult): string => {
  const target = result.target;
  if (!target || target.elementIndex === undefined) return "none";
  const match = ctx.elements.find(
    (entry) =>
      entry.elementIndex === target.elementIndex &&
      entry.slideIndex === target.slideIndex
  );
  return match?.element.tag ?? "unknown";
};

interface RunTotals {
  errorCount: number;
  recommendationCount: number;
  recommendationsEnabled: boolean;
  assetId: string;
}

const commonProperties = (
  ctx: BuilderContext,
  totals: RunTotals
): Record<string, string> => ({
  asset_id: totals.assetId,
  asset_type: ctx.assetType,
  location_id: ctx.locationId,
  error_count: String(totals.errorCount),
  recommendation_count: String(totals.recommendationCount),
  conditional_logic_count_bucket: countBucket(conditionalLogicCount(ctx)),
  custom_field_count_bucket: countBucket(customFieldCount(ctx)),
  recommendations_enabled: String(totals.recommendationsEnabled),
});

/**
 * One event per distinct rule, not per finding: three unlabelled fields are one
 * signal about one rule, and emitting three would skew every rate we measure.
 */
export const trackRulesDetected = (
  ctx: BuilderContext,
  results: RuleResult[],
  totals: RunTotals
): void => {
  const seen = new Set<string>();
  results.forEach((result) => {
    if (seen.has(result.ruleId)) return;
    seen.add(result.ruleId);
    emitPendoEvent(RULE_DETECTED, {
      ...commonProperties(ctx, totals),
      rule_id: result.ruleId,
      rule_type: result.severity,
      builder_section: result.section,
      element_type: elementTypeFor(ctx, result),
      // Accurate at the moment of detection; the resolved event carries the
      // other half of the story.
      is_resolved_before_save: "false",
    });
  });
};

export const trackRuleResolved = (
  ctx: BuilderContext,
  result: RuleResult,
  elapsedMs: number,
  totals: RunTotals
): void => {
  emitPendoEvent(RULE_RESOLVED, {
    ...commonProperties(ctx, totals),
    rule_id: result.ruleId,
    rule_type: result.severity,
    builder_section: result.section,
    time_to_resolve_bucket: timeBucket(elapsedMs),
  });
};

export const __testables = { countBucket, timeBucket };
